import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  Settings, Calendar, Plus, Trash2, Download, Loader2, 
  Building, Clock, AlertTriangle, CheckCircle, Info,
  Smartphone, Monitor, Copy, ExternalLink, RefreshCw, Key, Users, Pencil,
  Wrench, Search, ArrowRight, Database
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PRODUCTION_DOMAIN = 'https://jornyx.com.br';

const ConfiguracoesPage = () => {
  const { api, user, token } = useAuth();
  const [activeTab, setActiveTab] = useState('feriados');
  const [loading, setLoading] = useState(true);
  
  // Feriados state
  const [feriados, setFeriados] = useState([]);
  const [importando, setImportando] = useState(false);
  const [anoImportar, setAnoImportar] = useState(new Date().getFullYear());
  const [showNovoFeriado, setShowNovoFeriado] = useState(false);
  const [novoFeriado, setNovoFeriado] = useState({
    data: '',
    descricao: '',
    tipo: 'empresa'
  });
  
  // Empresa info
  const [empresa, setEmpresa] = useState(null);
  
  // Empregados para diagnóstico
  const [empregados, setEmpregados] = useState([]);
  
  // Apps tokens state
  const [appTokens, setAppTokens] = useState({
    empresa_token: null,
    empresa_codigo: null
  });
  const [gerandoToken, setGerandoToken] = useState(false);
  
  // Sindicatos state
  const [sindicatos, setSindicatos] = useState([]);
  const [loadingSindicatos, setLoadingSindicatos] = useState(false);
  const [showNovoSindicato, setShowNovoSindicato] = useState(false);
  const [editandoSindicato, setEditandoSindicato] = useState(null);
  const [novoSindicato, setNovoSindicato] = useState({
    nome: '',
    sigla: '',
    tipo: 'sindicato',
    descricao: '',
    periodo_noturno_inicio: '22:00',
    periodo_noturno_fim: '05:00',
    extensao_adicional_noturno: false
  });
  
  // Estados para migração de marcações
  const [migracaoLoading, setMigracaoLoading] = useState(false);
  const [migracaoPreview, setMigracaoPreview] = useState(null);
  const [migracaoConfirmOpen, setMigracaoConfirmOpen] = useState(false);
  const [migracaoExecutando, setMigracaoExecutando] = useState(false);
  const [migracaoResultado, setMigracaoResultado] = useState(null);
  
  // Estados para logs de processamento
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsFiltroNivel, setLogsFiltroNivel] = useState('todos');
  const [logsFiltroData, setLogsFiltroData] = useState('');
  const [logsFiltroEmpregado, setLogsFiltroEmpregado] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    await Promise.all([
      fetchFeriados(),
      fetchEmpresa(),
      fetchAppTokens(),
      fetchSindicatos(),
      fetchEmpregados()
    ]);
    setLoading(false);
  };

  const fetchAppTokens = async () => {
    try {
      const response = await fetch(`${API_URL}/api/empresa/app-tokens`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAppTokens(data);
      }
    } catch (error) {
      console.error('Error fetching app tokens:', error);
    }
  };

  const gerarNovoTokenEmpresa = async () => {
    setGerandoToken(true);
    try {
      const response = await fetch(`${API_URL}/api/empresa/gerar-token-totem`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAppTokens(prev => ({ ...prev, empresa_token: data.token }));
        toast.success('Token do Totem gerado com sucesso!');
      } else {
        toast.error('Erro ao gerar token');
      }
    } catch (error) {
      toast.error('Erro ao gerar token');
    } finally {
      setGerandoToken(false);
    }
  };

  const copiarParaClipboard = (texto, descricao) => {
    navigator.clipboard.writeText(texto).then(() => {
      toast.success(`${descricao} copiado!`);
    }).catch(() => {
      toast.error('Erro ao copiar');
    });
  };

  const fetchEmpregados = async () => {
    try {
      const response = await api.get('/empregados?limit=500');
      setEmpregados(response.data.empregados || response.data || []);
    } catch (error) {
      console.error('Erro ao buscar empregados:', error);
    }
  };

  // URLs dos Apps
  const getAppEmpregadoUrl = () => {
    if (!empresa?.id) return '';
    return `${PRODUCTION_DOMAIN}/unified?empresa=${empresa.id}`;
  };

  const getAppTotemUrl = () => {
    if (!appTokens.empresa_token) return '';
    return `${PRODUCTION_DOMAIN}/empresa-ponto?token=${appTokens.empresa_token}`;
  };

  const fetchFeriados = async () => {
    try {
      const response = await api.get(`/feriados?empresa_id=${user?.empresa_id}`);
      setFeriados(response.data);
    } catch (error) {
      console.error('Error fetching feriados:', error);
    }
  };

  const fetchEmpresa = async () => {
    try {
      const response = await api.get(`/empresas/${user?.empresa_id}`);
      setEmpresa(response.data);
    } catch (error) {
      console.error('Error fetching empresa:', error);
    }
  };

  const importarFeriadosNacionais = async () => {
    setImportando(true);
    try {
      const response = await api.post(`/feriados/importar-nacionais?empresa_id=${user?.empresa_id}&ano=${anoImportar}`);
      toast.success(`${response.data.total} feriados importados com sucesso!`);
      fetchFeriados();
    } catch (error) {
      toast.error('Erro ao importar feriados');
    } finally {
      setImportando(false);
    }
  };

  const criarFeriado = async () => {
    if (!novoFeriado.data || !novoFeriado.descricao) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }
    
    try {
      await api.post('/feriados', {
        ...novoFeriado,
        empresa_id: user?.empresa_id
      });
      toast.success('Feriado criado com sucesso!');
      setShowNovoFeriado(false);
      setNovoFeriado({ data: '', descricao: '', tipo: 'empresa' });
      fetchFeriados();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar feriado');
    }
  };

  const excluirFeriado = async (id) => {
    if (!window.confirm('Confirma a exclusão deste feriado?')) return;
    
    try {
      await api.delete(`/feriados/${id}`);
      toast.success('Feriado excluído');
      fetchFeriados();
    } catch (error) {
      toast.error('Erro ao excluir feriado');
    }
  };

  // ===== SINDICATOS =====
  const fetchSindicatos = async () => {
    setLoadingSindicatos(true);
    try {
      const response = await api.get('/sindicatos');
      setSindicatos(response.data.filter(s => s.ativo !== false));
    } catch (error) {
      console.error('Erro ao carregar sindicatos:', error);
    } finally {
      setLoadingSindicatos(false);
    }
  };

  const criarSindicato = async () => {
    if (!novoSindicato.nome.trim()) {
      toast.error('Nome é obrigatório');
      return;
    }

    try {
      await api.post('/sindicatos', novoSindicato);
      toast.success('Sindicato/Categoria criado com sucesso');
      setShowNovoSindicato(false);
      setNovoSindicato({ nome: '', sigla: '', tipo: 'sindicato', descricao: '', periodo_noturno_inicio: '22:00', periodo_noturno_fim: '05:00', extensao_adicional_noturno: false });
      fetchSindicatos();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar sindicato');
    }
  };

  const atualizarSindicato = async () => {
    if (!editandoSindicato?.nome?.trim()) {
      toast.error('Nome é obrigatório');
      return;
    }

    try {
      await api.put(`/sindicatos/${editandoSindicato.id}`, {
        nome: editandoSindicato.nome,
        sigla: editandoSindicato.sigla,
        tipo: editandoSindicato.tipo,
        descricao: editandoSindicato.descricao,
        periodo_noturno_inicio: editandoSindicato.periodo_noturno_inicio || '22:00',
        periodo_noturno_fim: editandoSindicato.periodo_noturno_fim || '05:00',
        extensao_adicional_noturno: editandoSindicato.extensao_adicional_noturno || false
      });
      toast.success('Sindicato/Categoria atualizado');
      setEditandoSindicato(null);
      fetchSindicatos();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar');
    }
  };

  const excluirSindicato = async (id) => {
    if (!window.confirm('Confirma a desativação deste sindicato/categoria?')) return;
    
    try {
      await api.delete(`/sindicatos/${id}`);
      toast.success('Sindicato/Categoria desativado');
      fetchSindicatos();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao desativar');
    }
  };

  const getTipoBadge = (tipo) => {
    const tipos = {
      nacional: { label: 'Nacional', className: 'bg-blue-500' },
      estadual: { label: 'Estadual', className: 'bg-purple-500' },
      municipal: { label: 'Municipal', className: 'bg-green-500' },
      empresa: { label: 'Empresa', className: 'bg-amber-500' }
    };
    const config = tipos[tipo] || tipos.empresa;
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const [year, month, day] = dateStr.split('-');
    return `${day}/${month}/${year}`;
  };

  // Group feriados by year
  const feriadosByYear = feriados.reduce((acc, f) => {
    const year = f.data?.substring(0, 4) || 'Sem ano';
    if (!acc[year]) acc[year] = [];
    acc[year].push(f);
    return acc;
  }, {});

  const anos = Object.keys(feriadosByYear).sort((a, b) => b - a);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="configuracoes-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-800 font-['Outfit'] mb-1">Configurações</h2>
          <p className="text-slate-500">Configurações da empresa e parâmetros do sistema</p>
        </div>
      </div>

      {/* Empresa Info Card */}
      {empresa && (
        <Card className="border-slate-200 bg-gradient-to-r from-slate-50 to-white">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-emerald-100 rounded-xl flex items-center justify-center">
                <Building className="w-7 h-7 text-emerald-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-800">{empresa.nome_fantasia || empresa.razao_social}</h3>
                <p className="text-sm text-slate-500">CNPJ: {empresa.cnpj} | CNAE: {empresa.cnae}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-5 max-w-2xl">
          <TabsTrigger value="apps" className="flex items-center gap-2" data-testid="tab-apps">
            <Smartphone className="w-4 h-4" />
            Apps
          </TabsTrigger>
          <TabsTrigger value="sindicatos" className="flex items-center gap-2" data-testid="tab-sindicatos">
            <Users className="w-4 h-4" />
            Sindicatos
          </TabsTrigger>
          <TabsTrigger value="feriados" className="flex items-center gap-2" data-testid="tab-feriados">
            <Calendar className="w-4 h-4" />
            Feriados
          </TabsTrigger>
          <TabsTrigger value="manutencao" className="flex items-center gap-2" data-testid="tab-manutencao">
            <Wrench className="w-4 h-4" />
            Manutenção
          </TabsTrigger>
          <TabsTrigger value="geral" className="flex items-center gap-2" data-testid="tab-geral">
            <Settings className="w-4 h-4" />
            Geral
          </TabsTrigger>
        </TabsList>

        {/* Tab: Apps */}
        <TabsContent value="apps" className="mt-6 space-y-6">
          {/* App do Empregado */}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg font-['Outfit'] flex items-center gap-2">
                <Smartphone className="w-5 h-5 text-emerald-600" />
                App do Empregado
              </CardTitle>
              <CardDescription>
                Link para os funcionários acessarem o app de ponto, justificativas e comprovantes
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                <Label className="text-xs text-slate-500 mb-2 block">Link de Acesso</Label>
                <div className="flex items-center gap-2">
                  <Input 
                    value={getAppEmpregadoUrl()} 
                    readOnly 
                    className="font-mono text-sm bg-white"
                    data-testid="input-link-empregado"
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => copiarParaClipboard(getAppEmpregadoUrl(), 'Link do App Empregado')}
                    title="Copiar link"
                    data-testid="btn-copiar-link-empregado"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => window.open(getAppEmpregadoUrl(), '_blank')}
                    title="Abrir em nova aba"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              
              <div className="flex items-start gap-3 text-sm text-slate-600 bg-blue-50 rounded-lg p-3 border border-blue-200">
                <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-blue-800">Como compartilhar:</p>
                  <p className="text-blue-700">
                    Copie o link e envie para os funcionários via WhatsApp, email ou mural. 
                    Ao acessar, eles farão login com CPF e verão automaticamente esta empresa.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* App do Totem/Empresa */}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg font-['Outfit'] flex items-center gap-2">
                <Monitor className="w-5 h-5 text-blue-600" />
                App do Totem / Tablet
              </CardTitle>
              <CardDescription>
                Link para configurar o totem de registro de ponto com reconhecimento facial
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {appTokens.empresa_token ? (
                <>
                  <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                    <Label className="text-xs text-slate-500 mb-2 block">Link de Acesso (com token permanente)</Label>
                    <div className="flex items-center gap-2">
                      <Input 
                        value={getAppTotemUrl()} 
                        readOnly 
                        className="font-mono text-sm bg-white"
                        data-testid="input-link-totem"
                      />
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => copiarParaClipboard(getAppTotemUrl(), 'Link do Totem')}
                        title="Copiar link"
                        data-testid="btn-copiar-link-totem"
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => window.open(getAppTotemUrl(), '_blank')}
                        title="Abrir em nova aba"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between bg-emerald-50 rounded-lg p-3 border border-emerald-200">
                    <div className="flex items-center gap-2">
                      <Key className="w-4 h-4 text-emerald-600" />
                      <span className="text-sm text-emerald-700 font-medium">Token ativo</span>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={gerarNovoTokenEmpresa}
                      disabled={gerandoToken}
                      className="text-amber-600 border-amber-300 hover:bg-amber-50"
                    >
                      {gerandoToken ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-1" />
                      ) : (
                        <RefreshCw className="w-4 h-4 mr-1" />
                      )}
                      Renovar Token
                    </Button>
                  </div>
                </>
              ) : (
                <div className="text-center py-6">
                  <Monitor className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p className="text-slate-500 mb-4">Nenhum token configurado para o totem</p>
                  <Button
                    onClick={gerarNovoTokenEmpresa}
                    disabled={gerandoToken}
                    className="bg-blue-600 hover:bg-blue-700"
                    data-testid="btn-gerar-token"
                  >
                    {gerandoToken ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Key className="w-4 h-4 mr-2" />
                    )}
                    Gerar Token do Totem
                  </Button>
                </div>
              )}
              
              <div className="flex items-start gap-3 text-sm text-slate-600 bg-amber-50 rounded-lg p-3 border border-amber-200">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-800">Importante:</p>
                  <p className="text-amber-700">
                    O token é permanente e dá acesso ao totem sem necessidade de login. 
                    Renove-o apenas se suspeitar de vazamento ou comprometimento.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Sindicatos */}
        <TabsContent value="sindicatos" className="mt-6 space-y-6">
          <Card className="border-slate-200">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg font-['Outfit'] flex items-center gap-2">
                  <Users className="w-5 h-5 text-emerald-600" />
                  Sindicatos e Categorias
                </CardTitle>
                <CardDescription>
                  Gerencie sindicatos e categorias para aplicar faixas de percentuais específicas
                </CardDescription>
              </div>
              <Button 
                onClick={() => setShowNovoSindicato(true)}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="btn-novo-sindicato"
              >
                <Plus className="w-4 h-4 mr-2" />
                Novo
              </Button>
            </CardHeader>
            <CardContent>
              {loadingSindicatos ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-emerald-600" />
                </div>
              ) : sindicatos.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Users className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>Nenhum sindicato/categoria cadastrado</p>
                  <p className="text-sm mt-1">Cadastre sindicatos para aplicar regras específicas de horas extras</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nome</TableHead>
                      <TableHead>Sigla</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Período Noturno</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sindicatos.map((sindicato) => (
                      <TableRow key={sindicato.id}>
                        <TableCell className="font-medium">{sindicato.nome}</TableCell>
                        <TableCell>{sindicato.sigla || '-'}</TableCell>
                        <TableCell>
                          <Badge variant={sindicato.tipo === 'sindicato' ? 'default' : 'secondary'}>
                            {sindicato.tipo === 'sindicato' ? 'Sindicato' : 'Categoria'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-slate-600 text-sm">
                          {sindicato.periodo_noturno_inicio || '22:00'} - {sindicato.periodo_noturno_fim || '05:00'}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setEditandoSindicato(sindicato)}
                              data-testid={`btn-editar-sindicato-${sindicato.id}`}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => excluirSindicato(sindicato.id)}
                              className="text-red-600 hover:text-red-700"
                              data-testid={`btn-excluir-sindicato-${sindicato.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Info Card */}
          <Card className="border-blue-200 bg-blue-50">
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-blue-600 mt-0.5" />
                <div className="text-sm text-blue-700">
                  <p className="font-medium mb-1">Como funciona?</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>Cadastre sindicatos ou categorias profissionais</li>
                    <li>Configure o período noturno conforme convenção coletiva (padrão CLT: 22:00-05:00)</li>
                    <li>Vincule empregados ao seu sindicato/categoria</li>
                    <li>Em Config. Eventos, crie faixas de percentuais específicas para cada sindicato</li>
                    <li>O motor aplicará automaticamente as regras corretas para cada empregado</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Feriados */}
        <TabsContent value="feriados" className="mt-6 space-y-6">
          {/* Import Section */}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg font-['Outfit'] flex items-center gap-2">
                <Download className="w-5 h-5" />
                Importar Feriados Nacionais
              </CardTitle>
              <CardDescription>
                Importe automaticamente os feriados nacionais brasileiros, incluindo feriados móveis (Carnaval, Páscoa, Corpus Christi)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col sm:flex-row items-start sm:items-end gap-4">
                <div className="space-y-2">
                  <Label>Ano</Label>
                  <Select value={String(anoImportar)} onValueChange={(v) => setAnoImportar(parseInt(v))}>
                    <SelectTrigger className="w-[180px]" data-testid="select-ano-importar">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[2024, 2025, 2026, 2027, 2028].map(ano => (
                        <SelectItem key={ano} value={String(ano)}>{ano}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  onClick={importarFeriadosNacionais}
                  disabled={importando}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="btn-importar-feriados"
                >
                  {importando ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4 mr-2" />
                  )}
                  {importando ? 'Importando...' : 'Importar Feriados'}
                </Button>
              </div>
              <div className="mt-4 p-3 bg-blue-50 rounded-lg flex items-start gap-2 text-sm text-blue-700">
                <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>
                  Os feriados importados serão usados para calcular horas extras com adicional de 100% 
                  no motor de apuração de jornadas.
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Add Manual Holiday */}
          <Card className="border-slate-200">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg font-['Outfit']">Feriados Cadastrados</CardTitle>
                <CardDescription>Gerencie os feriados para cálculo de horas extras</CardDescription>
              </div>
              <Button
                onClick={() => setShowNovoFeriado(true)}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="btn-novo-feriado"
              >
                <Plus className="w-4 h-4 mr-2" />
                Novo Feriado
              </Button>
            </CardHeader>
            <CardContent>
              {feriados.length === 0 ? (
                <div className="text-center py-12">
                  <Calendar className="w-12 h-12 mx-auto mb-3 text-slate-400" />
                  <p className="text-slate-500">Nenhum feriado cadastrado</p>
                  <p className="text-sm text-slate-400 mt-1">
                    Importe os feriados nacionais ou adicione manualmente
                  </p>
                </div>
              ) : (
                <div className="space-y-6">
                  {anos.map(ano => (
                    <div key={ano}>
                      <h4 className="text-sm font-semibold text-slate-600 mb-3 flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        {ano}
                        <Badge variant="secondary" className="ml-2">
                          {feriadosByYear[ano].length} feriados
                        </Badge>
                      </h4>
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="w-[120px]">Data</TableHead>
                              <TableHead>Descrição</TableHead>
                              <TableHead className="w-[100px]">Tipo</TableHead>
                              <TableHead className="w-[80px] text-right">Ações</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {feriadosByYear[ano]
                              .sort((a, b) => a.data.localeCompare(b.data))
                              .map((feriado) => (
                                <TableRow key={feriado.id}>
                                  <TableCell className="font-medium">
                                    {formatDate(feriado.data)}
                                  </TableCell>
                                  <TableCell>{feriado.descricao}</TableCell>
                                  <TableCell>{getTipoBadge(feriado.tipo)}</TableCell>
                                  <TableCell className="text-right">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => excluirFeriado(feriado.id)}
                                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                      data-testid={`btn-excluir-feriado-${feriado.id}`}
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              ))}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Geral */}
        <TabsContent value="geral" className="mt-6">
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg font-['Outfit']">Configurações Gerais</CardTitle>
              <CardDescription>Configurações gerais da empresa</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3 mb-3">
                    <Clock className="w-5 h-5 text-slate-600" />
                    <span className="font-medium">Banco de Horas</span>
                  </div>
                  <p className="text-sm text-slate-500">
                    Configure os parâmetros do banco de horas na página dedicada
                  </p>
                  <Button
                    variant="outline"
                    className="mt-3"
                    onClick={() => window.location.href = '/empresa/banco-horas'}
                  >
                    Ir para Banco de Horas
                  </Button>
                </div>
                
                <div className="p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3 mb-3">
                    <AlertTriangle className="w-5 h-5 text-slate-600" />
                    <span className="font-medium">Alertas de Risco</span>
                  </div>
                  <p className="text-sm text-slate-500">
                    Configure os parâmetros de alertas de risco trabalhista
                  </p>
                  <Button
                    variant="outline"
                    className="mt-3"
                    onClick={() => window.location.href = '/empresa/riscos'}
                  >
                    Ir para Riscos
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Manutenção */}
        <TabsContent value="manutencao" className="mt-6 space-y-6">
          <Card className="border-slate-200">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Database className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <CardTitle className="text-lg font-['Outfit']">Migração de Marcações Antigas</CardTitle>
                  <CardDescription>
                    Atribua slots (E1, S1, E2, S2...) às marcações inseridas antes da implementação do sistema de posição fixa
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Informações sobre o processo */}
              <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                <h4 className="font-medium text-slate-900 mb-2 flex items-center gap-2">
                  <Wrench className="w-4 h-4" />
                  O que será feito
                </h4>
                <ul className="text-sm text-slate-600 space-y-1 list-disc list-inside">
                  <li>Busca marcações sem campo <code className="bg-slate-200 px-1 rounded">slot</code> definido</li>
                  <li>Agrupa por empregado e data</li>
                  <li>Ordena por horário (ordem cronológica)</li>
                  <li><strong>Horários duplicados:</strong> mantém apenas um, exclui os demais</li>
                  <li>Atribui slots: E1 → S1 → E2 → S2 → E3 → S3</li>
                </ul>
              </div>

              {/* Botões de Ação */}
              <div className="flex gap-3">
                <Button 
                  onClick={async () => {
                    setMigracaoLoading(true);
                    setMigracaoPreview(null);
                    setMigracaoResultado(null);
                    try {
                      const response = await api.post(`/admin/migrar-marcacoes-sem-slot?simular=true&empresa_id=${user?.empresa_id}`);
                      setMigracaoPreview(response.data);
                      if (response.data.total_marcacoes_sem_slot === 0) {
                        toast.info('Nenhuma marcação antiga para migrar');
                      } else {
                        toast.success(`Encontradas ${response.data.total_marcacoes_sem_slot} marcação(ões) para migrar`);
                      }
                    } catch (error) {
                      toast.error(error.response?.data?.detail || 'Erro ao buscar marcações');
                    } finally {
                      setMigracaoLoading(false);
                    }
                  }}
                  disabled={migracaoLoading}
                  variant="outline"
                  className="flex items-center gap-2"
                  data-testid="btn-preview-migracao"
                >
                  {migracaoLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Search className="w-4 h-4" />
                  )}
                  {migracaoLoading ? 'Analisando...' : 'Simular Migração'}
                </Button>
                
                {migracaoPreview && migracaoPreview.total_marcacoes_sem_slot > 0 && (
                  <Button 
                    onClick={() => setMigracaoConfirmOpen(true)}
                    className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700"
                    data-testid="btn-executar-migracao"
                  >
                    <ArrowRight className="w-4 h-4" />
                    Executar Migração
                  </Button>
                )}
              </div>

              {/* Preview da migração */}
              {migracaoPreview && migracaoPreview.total_marcacoes_sem_slot > 0 && (
                <div className="mt-6">
                  <div className="flex items-center gap-2 mb-4">
                    <AlertTriangle className="w-5 h-5 text-blue-500" />
                    <h4 className="font-medium text-slate-900">
                      {migracaoPreview.total_marcacoes_sem_slot} marcação(ões) encontrada(s)
                    </h4>
                  </div>
                  
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
                    <div className="p-3 bg-blue-50 rounded-lg text-center">
                      <p className="text-2xl font-bold text-blue-700">{migracaoPreview.total_dias_processados}</p>
                      <p className="text-xs text-blue-600">Dias a processar</p>
                    </div>
                    <div className="p-3 bg-green-50 rounded-lg text-center">
                      <p className="text-2xl font-bold text-green-700">{migracaoPreview.total_migradas}</p>
                      <p className="text-xs text-green-600">Receberão slot</p>
                    </div>
                    <div className="p-3 bg-amber-50 rounded-lg text-center">
                      <p className="text-2xl font-bold text-amber-700">{migracaoPreview.total_duplicadas_excluidas}</p>
                      <p className="text-xs text-amber-600">Duplicadas a excluir</p>
                    </div>
                    <div className="p-3 bg-slate-100 rounded-lg text-center">
                      <p className="text-2xl font-bold text-slate-700">{migracaoPreview.total_marcacoes_sem_slot}</p>
                      <p className="text-xs text-slate-600">Total sem slot</p>
                    </div>
                  </div>
                  
                  {migracaoPreview.detalhes && migracaoPreview.detalhes.length > 0 && (
                    <div className="border rounded-lg overflow-hidden">
                      <div className="max-h-64 overflow-y-auto">
                        <Table>
                          <TableHeader className="sticky top-0 bg-white">
                            <TableRow>
                              <TableHead>Data</TableHead>
                              <TableHead>Marcações Migradas</TableHead>
                              <TableHead>Duplicadas</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {migracaoPreview.detalhes.map((item, idx) => (
                              <TableRow key={idx} className="bg-blue-50/30">
                                <TableCell className="font-medium font-mono text-sm">
                                  {item.data}
                                </TableCell>
                                <TableCell className="text-sm">
                                  {(item.migradas || []).map((m, i) => (
                                    <Badge key={i} variant="outline" className="mr-1 mb-1 text-green-600 border-green-300">
                                      {m.hora} → {m.slot}
                                    </Badge>
                                  ))}
                                  {(!item.migradas || item.migradas.length === 0) && (
                                    <span className="text-slate-400">-</span>
                                  )}
                                </TableCell>
                                <TableCell className="text-sm">
                                  {(item.duplicadas || []).length > 0 ? (
                                    (item.duplicadas || []).map((d, i) => (
                                      <Badge key={i} variant="outline" className="mr-1 mb-1 text-amber-600 border-amber-300">
                                        {d.hora}
                                      </Badge>
                                    ))
                                  ) : (
                                    <span className="text-slate-400">-</span>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                      {migracaoPreview.detalhes.length >= 50 && (
                        <div className="p-2 bg-slate-100 text-center text-xs text-slate-500">
                          Exibindo primeiros 50 registros
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Resultado da migração */}
              {migracaoResultado && (
                <div className="mt-6 p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                  <div className="flex items-center gap-2 mb-3">
                    <CheckCircle className="w-5 h-5 text-emerald-600" />
                    <h4 className="font-medium text-emerald-900">Migração Concluída</h4>
                  </div>
                  
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-emerald-700">{migracaoResultado.total_migradas}</p>
                      <p className="text-xs text-emerald-600">Marcações migradas</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-amber-700">{migracaoResultado.total_duplicadas_excluidas}</p>
                      <p className="text-xs text-amber-600">Duplicadas excluídas</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-slate-700">{migracaoResultado.total_dias_processados}</p>
                      <p className="text-xs text-slate-600">Dias processados</p>
                    </div>
                  </div>
                  
                  <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                    <p className="text-sm text-blue-800">
                      <strong>Próximo passo:</strong> Vá em <strong>Apontamentos → Ajustes RH</strong> e clique em <strong>"Recalcular Jornadas"</strong> para processar as horas trabalhadas.
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Card de Limpeza de Marcações Sem Slot */}
          <Card className="border-slate-200">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <Trash2 className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <CardTitle className="text-lg font-['Outfit']">Limpar Marcações Sem Slot</CardTitle>
                  <CardDescription>
                    Remove marcações sem slot quando já existem marcações com slot no mesmo dia
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                <h4 className="font-medium text-slate-900 mb-2 flex items-center gap-2">
                  <Wrench className="w-4 h-4" />
                  O que será feito
                </h4>
                <ul className="text-sm text-slate-600 space-y-1 list-disc list-inside">
                  <li>Para cada dia/empregado, verifica se existem marcações COM slot definido</li>
                  <li>Se existirem marcações COM slot → <strong className="text-green-600">preserva essas</strong></li>
                  <li>Marcações SEM slot no mesmo dia → <strong className="text-red-600">EXCLUI DEFINITIVAMENTE</strong></li>
                  <li>Dias sem nenhuma marcação com slot não são afetados</li>
                </ul>
              </div>

              <div className="flex gap-3">
                <Button 
                  variant="outline"
                  onClick={async () => {
                    setMigracaoLoading(true);
                    try {
                      const response = await api.post('/admin/restaurar-marcacoes?simular=true');
                      const data = response.data;
                      if (data.total_excluidas === 0) {
                        toast.info('Nenhuma marcação sem slot para excluir');
                      } else {
                        toast.success(`${data.total_excluidas} marcação(ões) sem slot serão excluídas`);
                      }
                      setMigracaoPreview(data);
                    } catch (error) {
                      toast.error('Erro ao simular');
                    } finally {
                      setMigracaoLoading(false);
                    }
                  }}
                  disabled={migracaoLoading}
                  className="flex items-center gap-2"
                >
                  {migracaoLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                  Simular Limpeza
                </Button>

                {migracaoPreview && migracaoPreview.total_excluidas > 0 && (
                  <Button 
                    onClick={async () => {
                      if (!confirm(`Confirma?\n\n${migracaoPreview.total_excluidas} marcações SEM SLOT serão EXCLUÍDAS DEFINITIVAMENTE\n\nMarcações COM SLOT serão preservadas.`)) return;
                      
                      setMigracaoExecutando(true);
                      try {
                        const response = await api.post('/admin/restaurar-marcacoes?simular=false');
                        toast.success('Limpeza concluída!');
                        setMigracaoResultado(response.data);
                        setMigracaoPreview(null);
                      } catch (error) {
                        toast.error('Erro ao executar');
                      } finally {
                        setMigracaoExecutando(false);
                      }
                    }}
                    disabled={migracaoExecutando}
                    className="flex items-center gap-2 bg-red-600 hover:bg-red-700"
                  >
                    {migracaoExecutando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                    Executar Limpeza
                  </Button>
                )}
              </div>

              {migracaoPreview && migracaoPreview.detalhes && migracaoPreview.detalhes.length > 0 && (
                <div className="border rounded-lg overflow-hidden mt-4">
                  <div className="p-3 bg-slate-100 font-medium text-sm">
                    {migracaoPreview.dias_processados} dia(s) com marcações sem slot | {migracaoPreview.total_excluidas} a excluir
                  </div>
                  <div className="max-h-48 overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Empregado</TableHead>
                          <TableHead>Data</TableHead>
                          <TableHead>Preservar (com slot)</TableHead>
                          <TableHead>Excluir (sem slot)</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {migracaoPreview.detalhes.map((item, idx) => (
                          <TableRow key={idx}>
                            <TableCell className="text-sm">{item.empregado}</TableCell>
                            <TableCell className="font-mono text-xs">{item.data}</TableCell>
                            <TableCell>
                              {(item.com_slot || []).map((r, i) => (
                                <Badge key={i} variant="outline" className="mr-1 text-green-600 border-green-300 text-xs">
                                  {r.hora}→{r.slot}
                                </Badge>
                              ))}
                              {(!item.com_slot || item.com_slot.length === 0) && (
                                <span className="text-slate-400">-</span>
                              )}
                            </TableCell>
                            <TableCell>
                              {(item.excluidas || []).map((e, i) => (
                                <Badge key={i} variant="outline" className="mr-1 text-red-600 border-red-300 text-xs">
                                  {e.hora}
                                </Badge>
                              ))}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Card de Logs de Processamento */}
          <Card className="border-slate-200">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Search className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <CardTitle className="text-lg font-['Outfit']">Logs de Processamento</CardTitle>
                    <CardDescription>
                      Acompanhe os processos de cálculo de jornadas e identifique problemas
                    </CardDescription>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={async () => {
                    setLogsLoading(true);
                    try {
                      let url = `/logs-processamento?limit=100`;
                      if (logsFiltroNivel && logsFiltroNivel !== 'todos') {
                        url += `&nivel=${logsFiltroNivel}`;
                      }
                      if (logsFiltroData) {
                        url += `&data=${logsFiltroData}`;
                      }
                      const response = await api.get(url);
                      setLogs(response.data.logs || []);
                      if (response.data.logs?.length === 0) {
                        toast.info('Nenhum log encontrado');
                      }
                    } catch (error) {
                      toast.error('Erro ao buscar logs');
                    } finally {
                      setLogsLoading(false);
                    }
                  }}
                  className="flex items-center gap-2"
                  data-testid="btn-buscar-logs"
                >
                  {logsLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                  Atualizar
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Filtros */}
              <div className="flex gap-4 flex-wrap">
                <div className="flex-1 min-w-[150px]">
                  <label className="text-sm text-slate-600 mb-1 block">Nível</label>
                  <select
                    value={logsFiltroNivel}
                    onChange={(e) => setLogsFiltroNivel(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                  >
                    <option value="todos">Todos</option>
                    <option value="info">Info</option>
                    <option value="warning">Warning</option>
                    <option value="error">Error</option>
                  </select>
                </div>
                <div className="flex-1 min-w-[150px]">
                  <label className="text-sm text-slate-600 mb-1 block">Data</label>
                  <Input
                    type="date"
                    value={logsFiltroData}
                    onChange={(e) => setLogsFiltroData(e.target.value)}
                    className="text-sm"
                  />
                </div>
              </div>

              {/* Diagnóstico de Marcações */}
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <h4 className="font-medium text-amber-900 mb-3 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  Diagnóstico de Marcações
                </h4>
                <p className="text-sm text-amber-700 mb-3">
                  Se um empregado não está calculando corretamente, use esta ferramenta para ver TODAS as marcações no banco (sem filtros).
                </p>
                <div className="flex gap-3 items-end flex-wrap">
                  <div>
                    <label className="text-xs text-amber-700 block mb-1">Empregado</label>
                    <select
                      value={logsFiltroEmpregado}
                      onChange={(e) => setLogsFiltroEmpregado(e.target.value)}
                      className="w-64 px-3 py-2 border rounded-lg text-sm"
                    >
                      <option value="">Selecione...</option>
                      {empregados.map(emp => (
                        <option key={emp.id} value={emp.id}>{emp.nome}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-amber-700 block mb-1">Data</label>
                    <Input
                      type="date"
                      value={logsFiltroData}
                      onChange={(e) => setLogsFiltroData(e.target.value)}
                      className="w-40 text-sm"
                    />
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={!logsFiltroEmpregado || !logsFiltroData}
                    onClick={async () => {
                      try {
                        const response = await api.get(`/admin/diagnostico-marcacoes?empregado_id=${logsFiltroEmpregado}&data=${logsFiltroData}`);
                        const data = response.data;
                        toast.info(`${data.empregado_nome}: ${data.total_marcacoes} marcações encontradas`);
                        console.log('Diagnóstico:', data);
                        alert(`DIAGNÓSTICO - ${data.empregado_nome}\nData: ${data.data}\nTotal: ${data.total_marcacoes} marcações\n\n${data.marcacoes.map(m => `${m.hora} | status: ${m.status || 'NULL'} | slot: ${m.slot || 'NULL'}`).join('\n')}`);
                      } catch (error) {
                        toast.error('Erro ao buscar diagnóstico');
                      }
                    }}
                    className="bg-amber-100 hover:bg-amber-200 text-amber-800"
                  >
                    <Search className="w-4 h-4 mr-1" />
                    Diagnosticar
                  </Button>
                </div>
              </div>

              {/* Lista de Logs */}
              {logs.length > 0 ? (
                <div className="border rounded-lg overflow-hidden">
                  <div className="max-h-96 overflow-y-auto">
                    <Table>
                      <TableHeader className="sticky top-0 bg-white">
                        <TableRow>
                          <TableHead className="w-20">Nível</TableHead>
                          <TableHead className="w-24">Data</TableHead>
                          <TableHead>Empregado</TableHead>
                          <TableHead>Mensagem</TableHead>
                          <TableHead className="w-32">Hora Log</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {logs.map((log, idx) => (
                          <TableRow key={idx} className={
                            log.nivel === 'error' ? 'bg-red-50' :
                            log.nivel === 'warning' ? 'bg-amber-50' : ''
                          }>
                            <TableCell>
                              <Badge variant={
                                log.nivel === 'error' ? 'destructive' :
                                log.nivel === 'warning' ? 'outline' : 'secondary'
                              } className={
                                log.nivel === 'warning' ? 'text-amber-600 border-amber-300' : ''
                              }>
                                {log.nivel}
                              </Badge>
                            </TableCell>
                            <TableCell className="font-mono text-xs">
                              {log.data}
                            </TableCell>
                            <TableCell className="text-sm">
                              {log.empregado_nome || '-'}
                            </TableCell>
                            <TableCell className="text-sm">
                              <div>{log.mensagem}</div>
                              {log.detalhes && Object.keys(log.detalhes).length > 0 && (
                                <details className="mt-1">
                                  <summary className="text-xs text-slate-500 cursor-pointer">Ver detalhes</summary>
                                  <pre className="text-xs bg-slate-100 p-2 rounded mt-1 overflow-x-auto">
                                    {JSON.stringify(log.detalhes, null, 2)}
                                  </pre>
                                </details>
                              )}
                            </TableCell>
                            <TableCell className="text-xs text-slate-500">
                              {log.created_at ? new Date(log.created_at).toLocaleString('pt-BR') : '-'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Search className="w-12 h-12 mx-auto mb-2 opacity-30" />
                  <p>Clique em "Atualizar" para buscar os logs</p>
                  <p className="text-sm">Os logs são gerados ao processar/recalcular jornadas</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialog de confirmação da migração */}
      <Dialog open={migracaoConfirmOpen} onOpenChange={setMigracaoConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-blue-600 flex items-center gap-2">
              <Database className="w-5 h-5" />
              Confirmar Migração de Marcações
            </DialogTitle>
            <DialogDescription className="space-y-3">
              <p>Você está prestes a migrar:</p>
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg space-y-2">
                <p className="font-semibold text-blue-900">
                  {migracaoPreview?.total_migradas || 0} marcações receberão slot
                </p>
                <p className="text-amber-700">
                  {migracaoPreview?.total_duplicadas_excluidas || 0} marcações duplicadas serão excluídas
                </p>
              </div>
              <p className="text-slate-600 text-sm">
                As marcações duplicadas serão marcadas como "excluída" e podem ser consultadas no histórico se necessário.
              </p>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMigracaoConfirmOpen(false)} disabled={migracaoExecutando}>
              Cancelar
            </Button>
            <Button
              onClick={async () => {
                setMigracaoExecutando(true);
                try {
                  const response = await api.post(`/admin/migrar-marcacoes-sem-slot?simular=false&empresa_id=${user?.empresa_id}`);
                  setMigracaoResultado(response.data);
                  setMigracaoPreview(null);
                  toast.success('Migração executada com sucesso!');
                  setMigracaoConfirmOpen(false);
                } catch (error) {
                  toast.error(error.response?.data?.detail || 'Erro ao executar migração');
                } finally {
                  setMigracaoExecutando(false);
                }
              }}
              disabled={migracaoExecutando}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="confirmar-migracao"
            >
              {migracaoExecutando ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Migrando...
                </>
              ) : (
                <>
                  <ArrowRight className="w-4 h-4 mr-2" />
                  Executar Migração
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Novo Feriado */}
      <Dialog open={showNovoFeriado} onOpenChange={setShowNovoFeriado}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Novo Feriado</DialogTitle>
            <DialogDescription>
              Adicione um feriado específico da empresa ou regional
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Data *</Label>
              <Input
                type="date"
                value={novoFeriado.data}
                onChange={(e) => setNovoFeriado({...novoFeriado, data: e.target.value})}
                data-testid="input-data-feriado"
              />
            </div>
            <div className="space-y-2">
              <Label>Descrição *</Label>
              <Input
                placeholder="Ex: Aniversário da Cidade"
                value={novoFeriado.descricao}
                onChange={(e) => setNovoFeriado({...novoFeriado, descricao: e.target.value})}
                data-testid="input-descricao-feriado"
              />
            </div>
            <div className="space-y-2">
              <Label>Tipo</Label>
              <Select 
                value={novoFeriado.tipo} 
                onValueChange={(v) => setNovoFeriado({...novoFeriado, tipo: v})}
              >
                <SelectTrigger data-testid="select-tipo-feriado">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="nacional">Nacional</SelectItem>
                  <SelectItem value="estadual">Estadual</SelectItem>
                  <SelectItem value="municipal">Municipal</SelectItem>
                  <SelectItem value="empresa">Empresa</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNovoFeriado(false)}>
              Cancelar
            </Button>
            <Button
              className="bg-emerald-600 hover:bg-emerald-700"
              onClick={criarFeriado}
              data-testid="btn-salvar-feriado"
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Salvar Feriado
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Novo Sindicato */}
      <Dialog open={showNovoSindicato} onOpenChange={setShowNovoSindicato}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Novo Sindicato/Categoria</DialogTitle>
            <DialogDescription>
              Cadastre um sindicato ou categoria profissional para aplicar regras específicas
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nome *</Label>
                <Input
                  placeholder="Ex: SINTTEL-PR"
                  value={novoSindicato.nome}
                  onChange={(e) => setNovoSindicato({...novoSindicato, nome: e.target.value})}
                  data-testid="input-nome-sindicato"
                />
              </div>
              <div className="space-y-2">
                <Label>Sigla</Label>
                <Input
                  placeholder="Ex: SINTTEL"
                  value={novoSindicato.sigla}
                  onChange={(e) => setNovoSindicato({...novoSindicato, sigla: e.target.value})}
                  data-testid="input-sigla-sindicato"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Tipo</Label>
                <Select 
                  value={novoSindicato.tipo} 
                  onValueChange={(v) => setNovoSindicato({...novoSindicato, tipo: v})}
                >
                  <SelectTrigger data-testid="select-tipo-sindicato">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sindicato">Sindicato</SelectItem>
                    <SelectItem value="categoria">Categoria Profissional</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Descrição</Label>
                <Input
                  placeholder="Descrição opcional"
                  value={novoSindicato.descricao}
                  onChange={(e) => setNovoSindicato({...novoSindicato, descricao: e.target.value})}
                  data-testid="input-descricao-sindicato"
                />
              </div>
            </div>
            
            {/* Período Noturno */}
            <div className="border-t pt-4 mt-4">
              <Label className="text-sm font-medium text-slate-700 mb-3 block">
                Período Noturno (para cálculo de adicional)
              </Label>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs text-slate-500">Início</Label>
                  <Input
                    type="time"
                    value={novoSindicato.periodo_noturno_inicio}
                    onChange={(e) => setNovoSindicato({...novoSindicato, periodo_noturno_inicio: e.target.value})}
                    data-testid="input-periodo-noturno-inicio"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-slate-500">Fim</Label>
                  <Input
                    type="time"
                    value={novoSindicato.periodo_noturno_fim}
                    onChange={(e) => setNovoSindicato({...novoSindicato, periodo_noturno_fim: e.target.value})}
                    data-testid="input-periodo-noturno-fim"
                  />
                </div>
              </div>
              <p className="text-xs text-slate-500 mt-2">
                Padrão CLT: 22:00 às 05:00. Ajuste conforme convenção coletiva.
              </p>
              
              {/* Extensão Adicional Noturno - Súmula 60 */}
              <div className="flex items-start gap-3 mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <input
                  type="checkbox"
                  id="extensao-noturno-novo"
                  checked={novoSindicato.extensao_adicional_noturno || false}
                  onChange={(e) => setNovoSindicato({...novoSindicato, extensao_adicional_noturno: e.target.checked})}
                  className="mt-1"
                  data-testid="checkbox-extensao-noturno"
                />
                <div>
                  <Label htmlFor="extensao-noturno-novo" className="text-sm font-medium text-amber-800 cursor-pointer">
                    Extensão do adicional noturno (Súmula 60, Item II)
                  </Label>
                  <p className="text-xs text-amber-700 mt-1">
                    Quando a jornada inicia no período noturno e continua após o fim, 
                    todo o período trabalhado é considerado noturno para fins de adicional.
                  </p>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNovoSindicato(false)}>
              Cancelar
            </Button>
            <Button
              className="bg-emerald-600 hover:bg-emerald-700"
              onClick={criarSindicato}
              data-testid="btn-salvar-sindicato"
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Editar Sindicato */}
      <Dialog open={!!editandoSindicato} onOpenChange={() => setEditandoSindicato(null)}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Editar Sindicato/Categoria</DialogTitle>
          </DialogHeader>
          {editandoSindicato && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nome *</Label>
                  <Input
                    value={editandoSindicato.nome}
                    onChange={(e) => setEditandoSindicato({...editandoSindicato, nome: e.target.value})}
                    data-testid="input-edit-nome-sindicato"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Sigla</Label>
                  <Input
                    value={editandoSindicato.sigla || ''}
                    onChange={(e) => setEditandoSindicato({...editandoSindicato, sigla: e.target.value})}
                    data-testid="input-edit-sigla-sindicato"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Tipo</Label>
                  <Select 
                    value={editandoSindicato.tipo} 
                    onValueChange={(v) => setEditandoSindicato({...editandoSindicato, tipo: v})}
                  >
                    <SelectTrigger data-testid="select-edit-tipo-sindicato">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sindicato">Sindicato</SelectItem>
                      <SelectItem value="categoria">Categoria Profissional</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Descrição</Label>
                  <Input
                    value={editandoSindicato.descricao || ''}
                    onChange={(e) => setEditandoSindicato({...editandoSindicato, descricao: e.target.value})}
                    data-testid="input-edit-descricao-sindicato"
                  />
                </div>
              </div>
              
              {/* Período Noturno */}
              <div className="border-t pt-4 mt-4">
                <Label className="text-sm font-medium text-slate-700 mb-3 block">
                  Período Noturno (para cálculo de adicional)
                </Label>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs text-slate-500">Início</Label>
                    <Input
                      type="time"
                      value={editandoSindicato.periodo_noturno_inicio || '22:00'}
                      onChange={(e) => setEditandoSindicato({...editandoSindicato, periodo_noturno_inicio: e.target.value})}
                      data-testid="input-edit-periodo-noturno-inicio"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs text-slate-500">Fim</Label>
                    <Input
                      type="time"
                      value={editandoSindicato.periodo_noturno_fim || '05:00'}
                      onChange={(e) => setEditandoSindicato({...editandoSindicato, periodo_noturno_fim: e.target.value})}
                      data-testid="input-edit-periodo-noturno-fim"
                    />
                  </div>
                </div>
                <p className="text-xs text-slate-500 mt-2">
                  Padrão CLT: 22:00 às 05:00. Ajuste conforme convenção coletiva.
                </p>
                
                {/* Extensão Adicional Noturno - Súmula 60 */}
                <div className="flex items-start gap-3 mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <input
                    type="checkbox"
                    id="extensao-noturno-edit"
                    checked={editandoSindicato.extensao_adicional_noturno || false}
                    onChange={(e) => setEditandoSindicato({...editandoSindicato, extensao_adicional_noturno: e.target.checked})}
                    className="mt-1"
                    data-testid="checkbox-edit-extensao-noturno"
                  />
                  <div>
                    <Label htmlFor="extensao-noturno-edit" className="text-sm font-medium text-amber-800 cursor-pointer">
                      Extensão do adicional noturno (Súmula 60, Item II)
                    </Label>
                    <p className="text-xs text-amber-700 mt-1">
                      Quando a jornada inicia no período noturno e continua após o fim, 
                      todo o período trabalhado é considerado noturno para fins de adicional.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditandoSindicato(null)}>
              Cancelar
            </Button>
            <Button
              className="bg-emerald-600 hover:bg-emerald-700"
              onClick={atualizarSindicato}
              data-testid="btn-atualizar-sindicato"
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Atualizar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ConfiguracoesPage;
